import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart, getCartId, stockLimit } from "@/context/CartContext";
import { Minus, Plus, X } from "lucide-react";
import { useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);

  const total = Math.max(0.5, subtotal - (applied?.discount_amount || 0));

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discount/validate", {
        code: code.trim().toUpperCase(),
        subtotal,
      });
      setApplied(data);
      toast.success(`Code ${data.code} applied — −$${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setApplying(false);
    }
  };

  const removeCode = () => {
    setApplied(null);
    setCode("");
  };

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        discount_code: applied?.code || null,
        cart_id: getCartId(),
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col rs-silver-tray"
      >
        {/* Velvet lining — deep oxblood weave with subtle vertical folds */}
        <div aria-hidden="true" className="rs-tray-velvet" />
        {/* Brass edge frame — hairline gilt trim tracing the tray */}
        <div aria-hidden="true" className="rs-tray-brass" />

        <SheetHeader
          className="relative z-10 px-6 py-6"
          style={{ borderBottom: "1px solid rgba(212,175,55,0.35)" }}
        >
          <SheetTitle className="font-serif-display text-2xl text-bone text-left">
            The Silver Tray
          </SheetTitle>
          <div className="overline text-left" style={{ color: "#d4af37" }}>
            {items.length === 0 ? "Presented empty." : `${items.length} offering${items.length > 1 ? "s" : ""} laid out`}
          </div>
        </SheetHeader>

        <div className="relative z-10 flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {items.length === 0 && (
            <div className="text-muted-bone text-sm italic">
              The butler awaits. Choose an indulgence and it will be placed here.
            </div>
          )}
          {items.map((item) => {
            const limit = stockLimit(item);
            const atLimit = Number.isFinite(limit) && item.quantity >= limit;
            return (
            <div
              key={item.product_id}
              data-testid={`cart-item-${item.product_id}`}
              className="flex gap-4 rs-tray-item p-3"
            >
              <img
                src={resolveImg(item.image)}
                alt={item.name}
                className="w-24 h-32 object-cover"
                style={{ border: "1px solid rgba(212,175,55,0.35)" }}
              />
              <div className="flex-1 flex flex-col">
                <div className="font-serif-display text-lg text-bone leading-snug">
                  {item.name}
                </div>
                <div className="text-sm mt-1" style={{ color: "#d4af37" }}>
                  ${item.price.toFixed(2)}
                </div>
                {Number.isFinite(limit) && limit <= 5 && (
                  <div
                    data-testid={`cart-stock-hint-${item.product_id}`}
                    className="text-[10px] uppercase tracking-[0.2em] mt-1"
                    style={{ color: "#d4af37" }}
                  >
                    Only {limit} available
                  </div>
                )}
                <div className="mt-auto flex items-center justify-between">
                  <div
                    className="flex items-center border"
                    style={{ borderColor: "rgba(212,175,55,0.4)" }}
                  >
                    <button
                      data-testid={`cart-decrement-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      className="px-2 py-1 text-muted-bone hover:text-bone"
                      aria-label="decrease"
                    >
                      <Minus strokeWidth={1.5} size={14} />
                    </button>
                    <span className="px-3 text-sm text-bone">{item.quantity}</span>
                    <button
                      data-testid={`cart-increment-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      disabled={atLimit}
                      className="px-2 py-1 text-muted-bone hover:text-bone disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="increase"
                    >
                      <Plus strokeWidth={1.5} size={14} />
                    </button>
                  </div>
                  <button
                    data-testid={`cart-remove-${item.product_id}`}
                    onClick={() => removeItem(item.product_id)}
                    className="text-muted-bone hover:text-bone text-xs uppercase tracking-widest"
                  >
                    <X strokeWidth={1.5} size={16} />
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div
          className="relative z-10 p-6 space-y-4"
          style={{ borderTop: "1px solid rgba(212,175,55,0.35)" }}
        >
          {/* Discount code */}
          <div>
            <div className="overline mb-2" style={{ color: "#d4af37" }}>House cipher</div>
            {applied ? (
              <div
                data-testid="cart-discount-applied"
                className="flex items-center justify-between border px-3 py-2"
                style={{ borderColor: "rgba(212,175,55,0.55)", background: "rgba(60,10,15,0.35)" }}
              >
                <div className="text-sm">
                  <span className="text-bone font-mono">{applied.code}</span>
                  <span className="text-muted-bone ml-2">−${applied.discount_amount.toFixed(2)}</span>
                </div>
                <button
                  data-testid="cart-discount-remove"
                  onClick={removeCode}
                  className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div
                className="flex border"
                style={{ borderColor: "rgba(212,175,55,0.4)" }}
              >
                <input
                  data-testid="cart-discount-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="WELCOME10"
                  className="flex-1 bg-transparent px-3 py-2 text-sm text-bone placeholder:text-muted-bone outline-none font-mono"
                />
                <button
                  data-testid="cart-discount-apply"
                  onClick={applyCode}
                  disabled={applying || !code.trim()}
                  className="px-4 text-xs uppercase tracking-widest text-muted-bone hover:text-bone disabled:opacity-50"
                >
                  {applying ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-bone">Subtotal</span>
              <span data-testid="cart-subtotal" className="text-bone">${subtotal.toFixed(2)}</span>
            </div>
            {applied && (
              <div className="flex justify-between">
                <span className="text-muted-bone">Discount ({applied.code})</span>
                <span className="text-bone">−${applied.discount_amount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div
            className="flex justify-between items-baseline pt-4"
            style={{ borderTop: "1px solid rgba(212,175,55,0.35)" }}
          >
            <span className="overline" style={{ color: "#d4af37" }}>Total</span>
            <span data-testid="cart-total" className="font-serif-display text-2xl text-bone">
              ${total.toFixed(2)}
            </span>
          </div>
          <div className="text-xs text-muted-bone leading-relaxed italic">
            Taxes and discreet shipping settled at checkout. Plain paper. No trace.
          </div>
          <button
            data-testid="cart-checkout-btn"
            disabled={items.length === 0 || loading}
            onClick={checkout}
            className="rs-wax-seal-btn w-full disabled:opacity-50"
          >
            <span className="rs-wax-seal-inner">
              {loading ? "Preparing…" : "Seal & Send"}
            </span>
          </button>
        </div>

        <style>{`
          .rs-silver-tray {
            /* Deep polished-mahogany base under the velvet */
            background:
              radial-gradient(ellipse at 20% 0%, rgba(70,20,20,0.55) 0%, rgba(0,0,0,0) 55%),
              linear-gradient(180deg, #1a0d0d 0%, #0c0606 100%);
            border-left: 1px solid rgba(212,175,55,0.5);
            box-shadow:
              inset 6px 0 24px rgba(0,0,0,0.65),
              inset -1px 0 0 rgba(212,175,55,0.15),
              -20px 0 60px rgba(0,0,0,0.55);
          }
          .rs-tray-velvet {
            position: absolute;
            inset: 12px;
            pointer-events: none;
            z-index: 0;
            background:
              repeating-linear-gradient(
                90deg,
                rgba(80,15,20,0.55) 0px,
                rgba(55,10,15,0.55) 3px,
                rgba(75,12,18,0.55) 6px,
                rgba(50,8,12,0.55) 9px
              ),
              radial-gradient(ellipse at 50% 0%, rgba(130,25,35,0.35) 0%, rgba(0,0,0,0) 60%),
              linear-gradient(180deg, #4a0f18 0%, #1e0508 100%);
            box-shadow:
              inset 0 0 60px rgba(0,0,0,0.65),
              inset 0 0 0 1px rgba(0,0,0,0.6);
          }
          .rs-tray-brass {
            position: absolute;
            inset: 6px;
            pointer-events: none;
            z-index: 1;
            border: 1px solid rgba(212,175,55,0.55);
            box-shadow:
              inset 0 0 0 1px rgba(0,0,0,0.55),
              inset 0 0 12px rgba(212,175,55,0.12),
              0 0 0 1px rgba(0,0,0,0.4);
            background: linear-gradient(
              180deg,
              rgba(212,175,55,0.05) 0%,
              rgba(212,175,55,0) 8%,
              rgba(212,175,55,0) 92%,
              rgba(212,175,55,0.05) 100%
            );
          }
          .rs-tray-item {
            background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.25) 100%);
            border: 1px solid rgba(212,175,55,0.18);
          }

          /* Wax-seal checkout button — round-cornered oxblood pad pressed
             with a hairline brass ring. Hover deepens the emboss. */
          .rs-wax-seal-btn {
            position: relative;
            padding: 18px 24px;
            border: none;
            cursor: pointer;
            color: #f5f2eb;
            font-family: inherit;
            text-transform: uppercase;
            letter-spacing: 0.28em;
            font-size: 12px;
            background:
              radial-gradient(circle at 35% 30%, #c74c3d 0%, #8a1c1c 45%, #3a0808 100%);
            border-radius: 6px;
            box-shadow:
              inset 0 0 0 1px rgba(212,175,55,0.55),
              inset 0 0 0 3px rgba(0,0,0,0.35),
              inset 0 0 0 4px rgba(212,175,55,0.25),
              inset 0 6px 18px rgba(255,180,140,0.18),
              inset 0 -6px 18px rgba(0,0,0,0.6),
              0 6px 22px rgba(120,20,30,0.45);
            transition: transform 200ms ease-out, box-shadow 200ms ease-out;
          }
          .rs-wax-seal-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow:
              inset 0 0 0 1px rgba(230,190,80,0.75),
              inset 0 0 0 3px rgba(0,0,0,0.4),
              inset 0 0 0 4px rgba(230,190,80,0.35),
              inset 0 8px 20px rgba(255,200,160,0.24),
              inset 0 -8px 20px rgba(0,0,0,0.65),
              0 10px 30px rgba(160,30,40,0.6);
          }
          .rs-wax-seal-btn:active:not(:disabled) {
            transform: translateY(1px);
          }
          .rs-wax-seal-inner {
            position: relative;
            z-index: 1;
            text-shadow: 0 1px 0 rgba(0,0,0,0.55);
          }
        `}</style>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
